/**
 * PickerHeader — the workshop picker's title bar: a Back affordance to whatever
 * opened it, the heading, a one-line blurb, and a × that closes the whole flow.
 *
 * The blurb is a PROP, not derived here. The two callers promise different
 * things — "every region & month" when creating a booking, and the workshop
 * being moved off when rebooking, where every target is in one region — and a
 * header that promised the wrong breadth would be read before the filter row
 * below it was noticed.
 */
import { ChevronLeft, X } from 'lucide-react';

export interface PickerHeaderProps {
  onBack: () => void;
  onClose: () => void;
  titleId: string;
  /** Heading. Both callers use "Choose a workshop". */
  title?: string;
  /** One line under the heading, describing what is being chosen and how wide
   *  the choice is. */
  subtitle: React.ReactNode;
}

export function PickerHeader({
  onBack,
  onClose,
  titleId,
  title = 'Choose a workshop',
  subtitle,
}: PickerHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4 dark:border-stone-700">
      <div className="min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="mb-1 inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <h2
          id={titleId}
          className="text-base font-bold uppercase tracking-wide text-moss-900 dark:text-moss-300"
        >
          {title}
        </h2>
        <p className="text-xs text-stone-500">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="flex-shrink-0 rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600 dark:hover:bg-[#252b26]"
      >
        <X size={18} />
      </button>
    </div>
  );
}
